// Currency utility functions for frontend

// Currency symbols mapping
const CURRENCY_SYMBOLS = {
  'USD': '$',
  'CAD': 'C$',
  'GBP': '£',
  'EUR': '€',
  'AUD': 'A$',
  'JPY': '¥',
  'SGD': 'S$',
  'INR': '₹',
  'BRL': 'R$',
  'MXN': '$',
  'NZD': 'NZ$'
};

// Country to currency mapping
const COUNTRY_CURRENCY_MAP = {
  'US': 'USD',
  'CA': 'CAD',
  'GB': 'GBP',
  'AU': 'AUD',
  'DE': 'EUR',
  'FR': 'EUR',
  'IT': 'EUR',
  'ES': 'EUR',
  'NL': 'EUR',
  'JP': 'JPY',
  'SG': 'SGD',
  'IN': 'INR',
  'BR': 'BRL',
  'MX': 'MXN',
  'NZ': 'NZD'
};

/**
 * Get currency symbol for a given currency code
 * @param {string} currency - Currency code (e.g., 'USD', 'CAD')
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currency) {
  return CURRENCY_SYMBOLS[currency] || currency;
}

/**
 * Format a price with currency symbol
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted price string
 */
export function formatPrice(amount, currency = 'USD', decimals = 2) {
  const symbol = getCurrencySymbol(currency);
  const formattedAmount = parseFloat(amount).toFixed(decimals);
  
  // For some currencies, symbol goes after the amount
  if (currency === 'EUR') {
    return `${formattedAmount} ${symbol}`;
  }
  
  return `${symbol}${formattedAmount}`;
}

/**
 * Get currency for a given country code
 * @param {string} country - Country code (e.g., 'US', 'CA')
 * @returns {string} Currency code
 */
export function getCurrencyForCountry(country) {
  return COUNTRY_CURRENCY_MAP[country] || 'USD';
}

/**
 * Format price for display in components
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {object} Object with symbol, amount, and formatted string
 */
export function formatPriceForDisplay(amount, currency = 'USD') {
  const symbol = getCurrencySymbol(currency);
  const formattedAmount = parseFloat(amount).toFixed(2);
  
  return {
    symbol,
    amount: formattedAmount,
    formatted: `${symbol}${formattedAmount}`,
    currency
  };
}

/**
 * Parse price from string (removes currency symbols)
 * @param {string} priceString - Price string with or without currency symbol
 * @returns {number} Numeric price value
 */
export function parsePrice(priceString) {
  if (typeof priceString === 'number') return priceString;
  
  // Remove all currency symbols and non-numeric characters except decimal point
  const cleaned = priceString.replace(/[^\d.-]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Convert price between currencies (basic conversion rates)
 * Note: In production, you'd want to use real-time exchange rates
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency
 * @param {string} toCurrency - Target currency
 * @returns {number} Converted amount
 */
export function convertCurrency(amount, fromCurrency, toCurrency) {
  // Basic conversion rates (these should be updated with real-time rates)
  const rates = {
    'USD': 1,
    'CAD': 1.35,
    'GBP': 0.79,
    'EUR': 0.92,
    'AUD': 1.52,
    'JPY': 150,
    'SGD': 1.35,
    'INR': 83,
    'BRL': 5.1,
    'MXN': 17.5,
    'NZD': 1.62
  };
  
  if (fromCurrency === toCurrency) return amount;
  
  const fromRate = rates[fromCurrency] || 1;
  const toRate = rates[toCurrency] || 1;
  
  // Convert to USD first, then to target currency
  const usdAmount = amount / fromRate;
  return usdAmount * toRate;
}

export default {
  getCurrencySymbol,
  formatPrice,
  getCurrencyForCountry,
  formatPriceForDisplay,
  parsePrice,
  convertCurrency
};
